<template>
    <SettingsPage
        ref="SettingsPage"
        :schema="schema"
        :initial-value="currentSettings"
        :loading="loading"
        :updating="updating"
        title="Email Content"
        :description="description"
        @reset="reset"
        @save="save"
    >
        <template #preview="{ value }">
            <h3>Email Preview</h3>

            <ActionEmailPreview :value="value" :defaults="currentSettings" />

            <div class="SettingsPage__note">
                Note: The text displayed in the email above is for preview purposes only.
                The actual text used in your email is controlled on a per-template basis.
            </div>
        </template>

        <template #default="{ fields }">
            <div class="SettingsPage__formRow">
                <JsonSchemaCustomFormElement :field="fields.fromName" />
                <JsonSchemaCustomFormElement :field="fields.replyToName" />
            </div>

            <div class="SettingsPage__formRow">
                <JsonSchemaCustomFormElement :field="fields.replyToEmail" />
            </div>

            <hr>

            <h3>Requirement Link</h3>

            <div class="SettingsPage__formRow">
                <JsonSchemaCustomFormElement :field="fields.coiRequirementsLink" />
            </div>

            <hr>

            <h3>Fulfillment</h3>
            <div>
                <div class="SettingsPage__formRow">
                    <JsonSchemaCustomFormElement
                        :field="fields.fulfillmentConfiguration"
                        :custom-component-input="fulfillmentCustomComponents"
                        @input="onFulfillmentInput"
                    />
                </div>
            </div>
            <hr>

            <h3>Certificate Holder Address</h3>

            <div class="SettingsPage__formRow">
                <JsonSchemaCustomFormElement :field="fields.certificateHolderAddress" />
            </div>
        </template>
    </SettingsPage>
</template>

<style lang="scss">
    .EmailContentPage {
        &__checkbox {
            display: flex;
            align-items: center;

            .SwitchToggle {
                padding: 0;
                zoom: 0.9;
                margin-right: 15px;

                &--off .SwitchToggle__slider {
                    background-color: #778ea1;
                }

                &--on .SwitchToggle__slider {
                    background-color: #29b496;
                }
            }
        }
    }
</style>

<script lang="ts">
    import { markRaw } from 'vue';

    import { Component, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { JsonSchemaCustomFormElement } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import { SwitchToggle } from '@evidentid/dashboard-commons/components/SwitchToggle';
    import { RpFulfillmentConfig } from '@evidentid/gateway-api-client/src/models';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import EnableFulfillmentToggle from '@/modules/notifications-configuration/components/EnableFulfillmentToggle.vue';
    import { useNotificationsConfigurationStore } from '@/modules/notifications-configuration/store';
    import { usePageLayoutStore } from '@/modules/page-layout/store';

    import ActionEmailPreview from '../components/ActionEmailPreview.vue';
    import InputZoneSelector from '../components/InputZoneSelector.vue';
    import SettingsPage from '../components/SettingsPage.vue';
    import { buildEmailConfigurationSchema } from '../schemas/buildEmailConfigurationSchema';
    import {
        EmailConfiguration,
        NotificationsConfiguration,
        NotificationsConfigurationState,
    } from '../types';
    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';
    import { useRbacStore } from '@evidentid/dashboard-commons/modules/role-base-access-control/store';

    @Component({
        components: {
            InputZoneSelector,
            SettingsPage,
            ActionEmailPreview,
            JsonSchemaCustomFormElement,
            SwitchToggle,
        },
    })
    export default class EmailContent extends Vue {
        private get notificationsConfigurationStore() {
            return useNotificationsConfigurationStore();
        }

        private get pageLayoutStore() {
            return usePageLayoutStore();
        }

        private schema = buildEmailConfigurationSchema();
        // markRaw: a class property lands in reactive data, which would hand the
        // component to <component :is> as a reactive proxy — Vue warns about the
        // pointless overhead. This map is a constant lookup, never state.
        private fulfillmentCustomComponents = markRaw({ boolean: EnableFulfillmentToggle });
        private localSettings = this.getInitialLocalSettings();
        private description = 'Manage the sender details, requirement links, and certificate holder address included in emails sent to your entities.';

        private created(): void {
            this.pageLayoutStore.resetPageLayout();
        }

        private get rpName(): string {
            return this.$route.params.rpId as string;
        }

        private get state(): NotificationsConfigurationState {
            return this.notificationsConfigurationStore.$state;
        }

        private get status(): OperationStatus {
            return this.state.loadSettingsStatus[this.rpName] || OperationStatus.uninitialized;
        }

        private get loading(): boolean {
            return ![ OperationStatus.success, OperationStatus.error ].includes(this.status);
        }

        private get updateStatus(): OperationStatus {
            return this.state.updateSettingsStatus[this.rpName] || OperationStatus.uninitialized;
        }

        private get updating(): boolean {
            return this.updateStatus === OperationStatus.loading;
        }

        private get currentSettings(): NotificationsConfiguration | null {
            return this.state.settings[this.rpName] || null;
        }

        @Watch('loading', { immediate: true })
        private onLoadingChange(): void {
            this.pageLayoutStore.setLoading(this.loading);
        }

        @Watch('rpName', { immediate: true })
        private async onRpChange(newVal: string, oldVal: string): Promise<void> {
            if (newVal && newVal !== oldVal) {
                await useRbacStore().validateRbac([ RbacFeature.displayAndEmailContent ]);
            }
        }

        private getInitialLocalSettings(): Partial<NotificationsConfiguration> {
            return {
                fulfillmentConfiguration: { ...this.currentSettings?.fulfillmentConfiguration } as RpFulfillmentConfig,
            };
        }

        /**
         * If fulfillment disabled and then reEnabled, preserving the config since JsonSchemaForm will erase them.
         */
        private onFulfillmentInput(changedConfig: RpFulfillmentConfig): void {
            const { fulfillmentConfiguration } = this.localSettings;
            if (fulfillmentConfiguration && Object.keys(fulfillmentConfiguration).length > 0) {
                if (!fulfillmentConfiguration.enabled && changedConfig.enabled ||
                    fulfillmentConfiguration.enabled && !changedConfig.enabled) {
                    this.localSettings = {
                        ...this.localSettings,
                        fulfillmentConfiguration: {
                            ...fulfillmentConfiguration,
                            enabled: changedConfig.enabled,
                        },
                    };
                    (this.$refs.SettingsPage as SettingsPage).updateFormControllerValue(this.localSettings);
                    return;
                }
            }
            this.localSettings = {
                ...this.localSettings,
                fulfillmentConfiguration: { ...changedConfig },
            };
        }

        private reset(): void {
            this.localSettings = this.getInitialLocalSettings();
        }

        private async save(settings: EmailConfiguration): Promise<void> {
            const toSave =
                !settings.fulfillmentConfiguration?.enabled
                && Object.keys(settings.fulfillmentConfiguration || {}).length === 1
                    ? {
                        ...settings,
                        fulfillmentConfiguration: { ...this.localSettings.fulfillmentConfiguration, enabled: false },
                    }
                    : settings;
            await this.notificationsConfigurationStore.updateSettings({
                rpName: this.rpName,
                settings: toSave as EmailConfiguration,
            });

            if (this.updateStatus === OperationStatus.success) {
                useSnackbarStore().displaySnackbar({
                    message: 'Email configuration updated successfully.',
                    success: true,
                });
            }
        }

        @Watch('status', { immediate: true })
        private loadSettings(): void {
            if (this.status === OperationStatus.uninitialized) {
                this.notificationsConfigurationStore.loadSettings({ rpName: this.rpName });
            }
        }
    }
</script>
